// Getting started: the set-up steps for a new company, and the progress
// kept in this browser, per company. A step is done when its page saves
// during set-up, or when its data turns out to exist already. A step the
// company doesn't need yet can be skipped and done later from the Getting
// started page. Pages hear about changes through the 'onboarding:changed'
// event.

const ALL_STEPS = [
  // Chosen right on the Getting started page (inline), so it has no page of its own.
  { id: 'timezone', title: 'Time zone', what: 'Where your business is. Dates and times on sales, receipts and journal entries follow its clock.', route: '/onboarding', action: 'Choose', inline: true },
  { id: 'utilities', module: 'accounting', title: 'Chart of accounts', what: 'The accounts your books use. One click sets up the standard retail chart.', note: 'Needed before you can sell or buy.', route: '/settings/data', action: 'Set up' },
  { id: 'opening', module: 'accounting', title: 'Opening balances', what: 'Moving from another system? Enter what each account held on your start date.', note: 'A new business starts from zero: skip this.', route: '/master/opening-balances', action: 'Enter balances' },
  { id: 'locations', title: 'Locations', what: 'Your stores and warehouses: where you sell and keep stock.', route: '/master/locations', add: '/master/locations/new', action: 'Add a location' },
  { id: 'products', title: 'Products & services', what: 'What you sell, with prices and tax. Products can also come from a spreadsheet.', route: '/master/products', add: '/master/products/new', action: 'Add a product' },
  { id: 'vendors', module: 'purchase', title: 'Vendors', what: 'Who you buy from, for purchase orders and bills.', note: 'Skip if you don’t buy stock yet.', route: '/master/vendors', add: '/master/vendors/new', action: 'Add a vendor' },
]
// The steps for this company's plan: a step whose module isn't in the plan
// is left out. App sets this from the session; a live binding, so every
// importer sees the current list.
export let STEPS = ALL_STEPS
export const setPlanModules = (modules) => {
  STEPS = ALL_STEPS.filter((s) => !s.module || !Array.isArray(modules) || modules.includes(s.module))
}
export const FINISH_ROUTE = '/onboarding'
export const CHANGED_EVENT = 'onboarding:changed'

const PREFIX = 'onboarding_progress_'
const ACTIVE_SUFFIX = '_active_step'

const safeParse = (raw, fallback) => { try { return raw ? JSON.parse(raw) : fallback } catch { return fallback } }
const ids = (v) => (Array.isArray(v) ? v.map(String) : [])
const keyFor = (companyId) => `${PREFIX}${companyId}`
// Every company with progress or a step under way (the marker alone counts too).
const allKeys = () => {
  const keys = Object.keys(localStorage).filter((k) => k.startsWith(PREFIX))
  return [...new Set(keys.map((k) => (k.endsWith(ACTIVE_SUFFIX) ? k.slice(0, -ACTIVE_SUFFIX.length) : k)))].sort()
}

// The company's key, or (no company given) the one with a step under way, else the first.
const detectKey = (companyId) => {
  if (companyId) return keyFor(companyId)
  const keys = allKeys()
  return keys.find((k) => localStorage.getItem(`${k}${ACTIVE_SUFFIX}`)) || keys[0] || null
}

const load = (key) => {
  const p = safeParse(localStorage.getItem(key), {}) || {}
  return { completedStepIds: ids(p.completedStepIds), skippedStepIds: ids(p.skippedStepIds), currentStepIndex: Number(p.currentStepIndex) || 0 }
}
const announce = () => { try { window.dispatchEvent(new CustomEvent(CHANGED_EVENT)) } catch { /* not in a browser */ } }
const save = (key, progress) => { localStorage.setItem(key, JSON.stringify(progress)); announce() }
const setActive = (key, stepId) => { if (stepId) localStorage.setItem(`${key}${ACTIVE_SUFFIX}`, stepId); else localStorage.removeItem(`${key}${ACTIVE_SUFFIX}`) }

export const stepIndex = (stepId) => STEPS.findIndex((s) => s.id === stepId)

/** The first step after `afterId` (or from the start) that is neither done nor skipped, or null. */
export const nextUnfinished = (progress, afterId = null) => {
  const done = new Set(progress.completedStepIds)
  const skipped = new Set(progress.skippedStepIds)
  return STEPS.slice(afterId ? stepIndex(afterId) + 1 : 0).find((s) => !done.has(s.id) && !skipped.has(s.id)) || null
}

/** This browser's progress for a company (both lists empty when none). */
export const readProgress = (companyId) => load(keyFor(companyId))

export const getOnboardingContext = (companyId) => {
  const progressKey = detectKey(companyId)
  if (!progressKey) return null
  const activeStep = localStorage.getItem(`${progressKey}${ACTIVE_SUFFIX}`)
  return { progressKey, companyId: progressKey.replace(PREFIX, ''), activeStep, progress: load(progressKey), inOnboarding: !!activeStep }
}

/** What follows a step once it's done or skipped: { step, finishing }. */
export const nextAfter = (stepId, companyId) => {
  const ctx = getOnboardingContext(companyId)
  const step = nextUnfinished(ctx?.progress || { completedStepIds: [], skippedStepIds: [] }, stepId)
  return { step, finishing: !step }
}

// Moves on from a step: the next unfinished one becomes the one under way,
// or set-up finishes (none under way; the progress is kept so skipped steps
// stay listed to do later).
const advance = (ctx, progress, fromId) => {
  const next = nextUnfinished(progress, fromId)
  progress.currentStepIndex = next ? stepIndex(next.id) : STEPS.length - 1
  save(ctx.progressKey, progress)
  setActive(ctx.progressKey, next ? next.id : null)
  return { inOnboarding: true, done: !next, nextStepId: next?.id || null, nextRoute: next ? (next.add || next.route) : FINISH_ROUTE, progress }
}

/** Marks a step done and says where to go next. */
export const completeOnboardingStep = (stepId, companyId) => {
  const ctx = getOnboardingContext(companyId)
  if (!ctx) return { inOnboarding: false, done: false, nextRoute: null, nextStepId: null }
  if (stepIndex(stepId) === -1) return { inOnboarding: true, done: false, nextRoute: null, nextStepId: null }
  const progress = ctx.progress
  progress.completedStepIds = [...new Set([...progress.completedStepIds, stepId])]
  progress.skippedStepIds = progress.skippedStepIds.filter((s) => s !== stepId)
  return advance(ctx, progress, stepId)
}

/** Skips a step for now (it stays listed to do later) and says where to go next. */
export const skipOnboardingStep = (stepId, companyId) => {
  const ctx = getOnboardingContext(companyId)
  if (!ctx || stepIndex(stepId) === -1) return { inOnboarding: false, done: false, nextRoute: null, nextStepId: null }
  const progress = ctx.progress
  if (!progress.completedStepIds.includes(stepId)) progress.skippedStepIds = [...new Set([...progress.skippedStepIds, stepId])]
  return advance(ctx, progress, stepId)
}

/** Records that these steps' data exists already: done, however it got there. */
export const markStepsDone = (companyId, stepIds) => {
  const key = keyFor(companyId)
  const progress = load(key)
  const found = stepIds.filter((id) => stepIndex(id) !== -1 && !progress.completedStepIds.includes(id))
  if (!found.length) return progress
  progress.completedStepIds = [...progress.completedStepIds, ...found]
  progress.skippedStepIds = progress.skippedStepIds.filter((s) => !found.includes(s))
  // The step under way turning out done already: move on to the next.
  const active = localStorage.getItem(`${key}${ACTIVE_SUFFIX}`)
  if (active && progress.completedStepIds.includes(active)) {
    const next = nextUnfinished(progress)
    setActive(key, next ? next.id : null)
  }
  save(key, progress)
  return progress
}

/** Makes a step the one under way, so saving on its page moves on. */
export const startOnboardingStep = (companyId, stepId) => {
  const key = keyFor(companyId)
  if (localStorage.getItem(key) === null) localStorage.setItem(key, JSON.stringify(load(key)))
  setActive(key, stepId)
}

/** Ends set-up: no step is under way; the progress stays so skipped steps stay listed. */
export const finishOnboarding = (companyId) => { setActive(keyFor(companyId), null); announce() }

export const isInOnboardingMode = (companyId) => !!getOnboardingContext(companyId)?.inOnboarding
export const getCurrentOnboardingStep = (companyId) => getOnboardingContext(companyId)?.activeStep || null
